import { useState, useEffect } from 'react';
import axios from 'axios';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { guardWrite } from '../../lib/modeGuard';
import { useAppMode } from '../../contexts/AppModeContext';
import {
  Sparkles, Phone, MessageSquareText, Mail, CheckCircle2, AlertTriangle,
  Clock, Users, Calendar, Shield, Plus, Search, Pencil, Play, Pause,
  Copy, Trash2, Download, MoreHorizontal, ArrowRight, ChevronRight,
  Mic, Volume2, Settings, Eye, Headphones, ClipboardList, Loader2, X,
  Zap, Workflow,
} from 'lucide-react';
import { toast } from 'sonner';
import AgentAnalyticsPanel from '../../components/echo/agents/AgentAnalyticsPanel';
import VoiceStudio from './VoiceStudio';

/* ═══ DATA ═══ */
const PRE_BUILT_AGENTS = [
  { id: 'booking-link', name: 'Online Booking Outreach', channel: 'sms', channelIcon: MessageSquareText, desc: 'Texts patients your public booking link — reactivation, recalls, overdue wellness', trigger: 'Weekly batch · inserts /book/ link', campaign: 'booking_link_outreach' },
  { id: 'reminder', name: 'Appointment Reminder', channel: 'voice', channelIcon: Phone, desc: 'Calls patients 24h before visit, confirms or reschedules', trigger: '24h before appointment', campaign: 'appt_reminder' },
  { id: 'noshow', name: 'No-Show Rescue', channel: 'voice+sms', channelIcon: Phone, desc: 'Calls high-risk patients morning-of to secure confirmation', trigger: 'Morning of, >30% risk' },
  { id: 'waitlist', name: 'Waitlist Filler', channel: 'voice+sms', channelIcon: Phone, desc: 'Contacts waitlist patients when a slot opens, books them', trigger: 'When slot opens' },
  { id: 'welcome', name: 'New Patient Welcome', channel: 'sms', channelIcon: MessageSquareText, desc: 'Texts new patients with intake link, prep instructions', trigger: 'After booking' },
  { id: 'caregap', name: 'Care Gap Outreach', channel: 'sms', channelIcon: MessageSquareText, desc: 'Texts patients overdue for wellness visits — includes booking link', trigger: 'Weekly batch', campaign: 'booking_link_outreach' },
  { id: 'copay', name: 'Co-pay Reminder', channel: 'sms', channelIcon: MessageSquareText, desc: 'Texts patients day-before with co-pay amount', trigger: '24h before visit' },
  { id: 'followup', name: 'Post-Visit Follow-up', channel: 'sms', channelIcon: MessageSquareText, desc: 'Day-after check-in, links to review + follow-up booking', trigger: '24h after visit', campaign: 'booking_link_outreach' },
  { id: 'insurance', name: 'Insurance Verification', channel: 'voice', channelIcon: Phone, desc: 'Calls payer to verify patient eligibility', trigger: '48h before visit' },
  { id: 'rx', name: 'Prescription Reminder', channel: 'sms', channelIcon: MessageSquareText, desc: 'Medication adherence texts for chronic patients', trigger: 'Daily per schedule' },
  { id: 'inbound', name: 'Inbound Receptionist', channel: 'voice', channelIcon: Headphones, desc: 'Answers incoming calls, handles scheduling + FAQs', trigger: 'Incoming call' },
  { id: 'afterhours', name: 'After-Hours Answering', channel: 'voice', channelIcon: Headphones, desc: 'Takes messages + handles urgent triage after close', trigger: 'After hours call' },
  { id: 'recall', name: 'Recall Campaign', channel: 'sms+voice', channelIcon: Phone, desc: 'Reactivates inactive patients — SMS includes the booking link', trigger: 'Monthly batch', campaign: 'booking_link_outreach' },
];

const DEPLOYED_AGENTS = [
  { id: 'da-1', templateId: 'reminder', name: 'Appointment Reminder Agent', status: 'running', channel: 'voice', channelIcon: Phone, desc: 'Calls patients 24h before appointment to confirm or reschedule.', todayCalls: 23, confirmed: 19, rescheduled: 2, failed: 2, lastRun: '5 min ago', trigger: '24h before appointment', voice: 'Echo Natural Female', schedule: 'Mon-Fri, 9am-6pm', escalate: 'Front desk alert', successRate: 91, avgCallLen: '1.2 min', aptsAffected: 21, campaign: 'appt_reminder' },
  { id: 'da-2', templateId: 'noshow', name: 'No-Show Rescue Agent', status: 'running', channel: 'voice+sms', channelIcon: Phone, desc: 'Calls high-risk patients morning-of to secure confirmation.', todayCalls: 8, confirmed: 6, rescheduled: 1, failed: 1, lastRun: '12 min ago', trigger: 'Morning of, risk >30%', voice: 'Echo Natural Male', schedule: '7am-10am daily', escalate: 'Front desk', successRate: 87, avgCallLen: '1.8 min', aptsAffected: 7 },
  { id: 'da-3', templateId: 'inbound', name: 'Inbound Receptionist', status: 'running', channel: 'voice', channelIcon: Headphones, desc: 'Answers incoming patient calls, handles scheduling.', todayCalls: 16, confirmed: 14, rescheduled: 0, failed: 2, lastRun: '1 min ago', trigger: 'Incoming call', voice: 'Echo Natural Female', schedule: 'Mon-Fri, 8am-5pm', escalate: 'Call forward', successRate: 94, avgCallLen: '2.1 min', aptsAffected: 14 },
  { id: 'da-4', templateId: 'booking-link', name: 'Online Booking Outreach', status: 'running', channel: 'sms', channelIcon: MessageSquareText, desc: 'Texts patients the public booking link — recalls, overdue wellness, reactivations.', todayCalls: 42, confirmed: 17, rescheduled: 0, failed: 3, lastRun: '2 hr ago', trigger: 'Weekly batch · Tue 10am', voice: 'SMS only', schedule: 'Weekly · Tue 10am', escalate: 'Front desk', successRate: 40, avgCallLen: '—', aptsAffected: 17, campaign: 'booking_link_outreach' },
];

const DEMO_ACTIVITY_FEED = [
  { agent: 'Reminder Agent', action: 'Called Maria Garcia', result: 'Confirmed 9:30am appointment', time: '2 min ago', status: 'success' },
  { agent: 'Inbound Agent', action: 'Handled call from (512) 555-0192', result: 'Scheduled new patient for Fri 2pm', time: '5 min ago', status: 'success' },
  { agent: 'Waitlist Agent', action: 'Texted David Kim', result: 'Slot filled: Tue 2:00pm', time: '8 min ago', status: 'success' },
  { agent: 'Reminder Agent', action: 'Called James Taylor', result: 'No answer — left voicemail', time: '12 min ago', status: 'warning' },
  { agent: 'Inbound Agent', action: 'Handled call from (512) 555-0834', result: 'Rescheduled to next week', time: '18 min ago', status: 'success' },
  { agent: 'No-Show Agent', action: 'Called Robert Johnson', result: 'Confirmed will attend', time: '25 min ago', status: 'success' },
  { agent: 'Care Gap Agent', action: 'Texted Emily Davis', result: 'Booked wellness visit Apr 24', time: '32 min ago', status: 'success' },
  { agent: 'Reminder Agent', action: 'Called Lisa Brown', result: 'Patient requested callback', time: '40 min ago', status: 'warning' },
];

const STATUS_PILL = {
  running: { label: 'Running', bg: '#f0fdf4', color: '#3B6D11', dot: '#22c55e' },
  paused: { label: 'Paused', bg: '#f5f5f5', color: '#888', dot: '#aaa' },
  attention: { label: 'Needs attention', bg: '#fffbeb', color: '#854F0B', dot: '#f59e0b' },
  draft: { label: 'Draft', bg: '#f5f3ff', color: '#534AB7', dot: '#111111' },
};

const FEED_DOT = { success: '#22c55e', warning: '#f59e0b', error: '#ef4444', active: '#3b82f6' };

const ChannelIcon = ({ channel }) => {
  if (channel === 'voice') return <Phone style={{ width: 14, height: 14 }} />;
  if (channel === 'sms') return <MessageSquareText style={{ width: 14, height: 14 }} />;
  if (channel === 'email') return <Mail style={{ width: 14, height: 14 }} />;
  return <Phone style={{ width: 14, height: 14 }} />;
};

/* ═══ WIZARD STEPS DATA ═══ */
const JOBS = [
  { id: 'remind', label: 'Remind patients about appointments', icon: Phone },
  { id: 'fill', label: 'Fill open slots from waitlist', icon: Calendar },
  { id: 'collect', label: 'Collect intake / follow up with pts', icon: ClipboardList },
  { id: 'answer', label: 'Answer inbound calls & messages', icon: Headphones },
];
const VOICES = [
  { id: 'nat-female', name: 'Echo Natural Female', desc: 'Warm · Conversational' },
  { id: 'nat-male', name: 'Echo Natural Male', desc: 'Clear · Professional' },
  { id: 'calm-female', name: 'Echo Calm Female', desc: 'Gentle · Reassuring' },
  { id: 'friendly-male', name: 'Echo Friendly Male', desc: 'Upbeat · Approachable' },
];
const DEFAULT_SCRIPT = `Greet the patient by name and confirm their appointment for [appointment date and time] with [provider name].

Ask if they can make it or if they need to reschedule.

If they confirm: thank them and remind them to bring their insurance card.

If they need to reschedule: offer the next 3 available times and book the one they choose.

If no answer: leave a friendly voicemail with the callback number.`;

/* ═══ Campaign templates — Echo auto-injects the practice's public
   booking URL ({booking_link}) into any campaign of type
   `booking_link_outreach`. */
const CAMPAIGN_TEMPLATES = {
  booking_link_outreach: {
    default_audience: 'overdue_wellness',
    audience_options: [
      { id: 'overdue_wellness', label: 'Overdue for wellness (>12 mo)' },
      { id: 'inactive_6mo',     label: 'Inactive patients (6–12 mo)' },
      { id: 'no_show_recent',   label: 'Recent no-shows — reactivate' },
      { id: 'marketing_optin',  label: 'Marketing opt-ins' },
    ],
    schedule_options: [
      { id: 'one_shot', label: 'One-time blast' },
      { id: 'weekly',   label: 'Weekly batch' },
      { id: 'drip_3',   label: '3-touch drip (day 0 / 3 / 7)' },
    ],
    sms_script: `Hi [Patient first name], this is [Practice name]. It's been a while — book your next visit in under 30 seconds: {booking_link}

Reply STOP to opt out.`,
    voice_script: `Hi [Patient first name], this is Echo from [Practice name]. You're due for a visit — we've texted you a secure booking link, or press 1 to book now with me.`,
  },
  appt_reminder: {
    default_audience: 'upcoming_24h',
    audience_options: [
      { id: 'upcoming_24h', label: 'Upcoming in next 24 hours' },
      { id: 'upcoming_48h', label: 'Upcoming in next 48 hours' },
    ],
    schedule_options: [
      { id: 'hourly', label: 'Hourly (continuous)' },
      { id: 'morning', label: 'Morning batch (8 AM)' },
    ],
    sms_script: `Hi [Patient first name] — reminder: [appointment date and time] with [provider name]. Reply C to confirm, R to reschedule, or visit {booking_link} to self-reschedule.`,
    voice_script: DEFAULT_SCRIPT,
  },
};

const VARIABLES = ['Patient name', 'Appointment date', 'Appointment time', 'Provider name', 'Practice name', 'Copay amount', 'Callback number'];

/* ═══ MAIN COMPONENT ═══ */
const EchoAIAgents = () => {
  const [tab, setTab] = useState('overview');
  const [twilioConnected, setTwilioConnected] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agents, setAgents] = useState(DEPLOYED_AGENTS);
  const { isDemo } = useAppMode();

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardMode, setWizardMode] = useState(null); // 'template' | 'scratch'
  const [wName, setWName] = useState('');
  const [wJob, setWJob] = useState('');
  const [wChannels, setWChannels] = useState([]);
  const [wVoice, setWVoice] = useState('nat-female');
  const [wScript, setWScript] = useState(DEFAULT_SCRIPT);
  const [wTrigger, setWTrigger] = useState('auto');
  const [wTriggerTime, setWTriggerTime] = useState('24h');
  const [wDays, setWDays] = useState(['Mon','Tue','Wed','Thu','Fri']);
  const [wEscalations, setWEscalations] = useState({ human: true, urgent: true, billing: true, upset: false, long: false });
  const [deploying, setDeploying] = useState(false);
  const [deployed, setDeployed] = useState(false);

  /* Wizard extras — agentic quick-run + automated workflows + custom fields */
  const [wQuickRun, setWQuickRun] = useState('');
  const [wWorkflows, setWWorkflows] = useState({
    notConfirmedCancel: false,
    notConfirmedHours: 4,
    voicemailRetryText: true,
    escalateIfRisk: true,
  });
  const [wCustomFields, setWCustomFields] = useState([]); // {key, label, kind: 'text'|'select'|'bool', value}

  /* Campaign booking link — fetched once from the practice's widget config. */
  const [bookingLink, setBookingLink] = useState(null);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ariaone-token') || '';
        const { data } = await axios.get(`${API}/api/echo/booking-widget`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (live && data?.slug) {
          setBookingLink(`${window.location.origin}/book/${data.slug}`);
        }
      } catch (_) { /* silent — campaigns still work without a live URL */ }
    })();
    return () => { live = false; };
  }, []);

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'agents', label: 'My Agents' },
    { id: 'voice', label: 'Voice Studio' },
    { id: 'create', label: 'Create' },
    { id: 'settings', label: 'Settings' },
  ];

  const startFromTemplate = (tpl) => {
    setWName(tpl.name + ' Agent');
    const isVoice = tpl.channel.includes('voice');
    setWJob(isVoice ? 'remind' : 'collect');
    setWChannels(isVoice ? (tpl.channel.includes('sms') ? ['voice', 'sms'] : ['voice']) : ['sms']);

    // Pre-fill script from campaign template when this tile carries one.
    const camp = tpl.campaign ? CAMPAIGN_TEMPLATES[tpl.campaign] : null;
    if (camp) {
      const pick = isVoice ? camp.voice_script : camp.sms_script;
      const linkHref = bookingLink || 'https://yourpractice.com/book/your-slug';
      setWScript((pick || DEFAULT_SCRIPT).replace('{booking_link}', linkHref));
      if (tpl.campaign === 'booking_link_outreach') {
        toast.success('Booking link auto-inserted', {
          description: bookingLink ? `Inserted ${bookingLink}` : 'Publish your booking widget to auto-inject the live URL.',
        });
      }
    } else {
      setWScript(DEFAULT_SCRIPT);
    }

    setWizardMode('template');
    setWizardStep(1);
    setDeployed(false);
    setWizardOpen(true);
  };

  const startFromScratch = () => {
    setWName(''); setWJob(''); setWChannels([]); setWScript(DEFAULT_SCRIPT);
    setWizardMode('scratch'); setWizardStep(1); setDeployed(false); setWizardOpen(true);
  };

  const deployAgent = () => {
    guardWrite('Deploy AI agent', () => {
      setDeploying(true);
      setTimeout(() => {
        setDeploying(false); setDeployed(true);
        const newAgent = { id: `da-${Date.now()}`, name: wName || 'Custom Agent', status: 'running', channel: wChannels.join('+') || 'voice', channelIcon: Phone, desc: wScript.slice(0, 80) + '...', todayCalls: 0, confirmed: 0, rescheduled: 0, failed: 0, lastRun: 'Just now', trigger: wTriggerTime, voice: VOICES.find(v => v.id === wVoice)?.name, schedule: `${wDays.join(',')}, 9am-6pm`, escalate: 'Front desk', successRate: 0, avgCallLen: '0 min', aptsAffected: 0 };
        setAgents(prev => [...prev, newAgent]);
        toast.success('Agent deployed', { description: `${wName} is now live` });
      }, 2000);
    }, isDemo);
  };

  const toggleAgent = (id) => {
    guardWrite('Toggle AI agent', () => {
      setAgents(prev => prev.map(a => a.id === id ? { ...a, status: a.status === 'running' ? 'paused' : 'running' } : a));
      const agent = agents.find(a => a.id === id);
      toast.success(agent?.status === 'running' ? 'Agent paused' : 'Agent resumed');
    }, isDemo);
  };

  /* Mock "send now" — fire a batch of outbound messages through
     the campaign endpoint. Analytics tab picks them up automatically.
     In demo mode we bypass the backend and just update local state. */
  const [runningCampaign, setRunningCampaign] = useState(null);
  const [demoCampaignSends, setDemoCampaignSends] = useState([]);
  const runCampaign = (agent) => {
    const demoSend = () => {
      setRunningCampaign(agent.id);
      setTimeout(() => {
        const names = ['Jamie Alvarez', 'Priya Shah', 'Marcus Lee', 'Rita Okafor', 'Ben Kowalski'];
        const link = `${window.location.origin}/book/riverside-family-medicine`;
        const now = Date.now();
        const sends = names.map((n, i) => ({
          send_id: `demo_${now}_${i}`,
          to_name: n,
          to: `(512) 555-01${String(30 + i).padStart(2, '0')}`,
          channel: 'sms',
          kind: 'booking_link_outreach',
          body: `Hi ${n.split(' ')[0]}, it's time for your visit — book in 30 seconds: ${link}`,
          status: i === 4 ? 'failed' : 'sent',
          sent_at: new Date(now - i * 3000).toISOString(),
          agent_id: agent.id,
          agent_name: agent.name,
        }));
        setDemoCampaignSends((prev) => [...sends, ...prev]);
        setAgents(prev => prev.map(a => a.id === agent.id ? {
          ...a,
          todayCalls: (a.todayCalls || 0) + 5,
          confirmed: (a.confirmed || 0) + 2,
          lastRun: 'Just now',
        } : a));
        toast.success('Sent 4 messages', { description: '1 failed · logged to Analytics (Demo).' });
        setRunningCampaign(null);
      }, 800);
    };
    if (isDemo) return demoSend();

    setRunningCampaign(agent.id);
    (async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ariaone-token') || '';
        const { data } = await axios.post(
          `${API}/api/echo/agents/campaign/run`,
          {
            agent_id: agent.id,
            agent_name: agent.name,
            channel: agent.channel.includes('sms') ? 'sms' : 'voice',
            kind: 'booking_link_outreach',
            count: 5,
            template_body: 'Hi {name}, it\'s time for your visit — book in 30 seconds: {booking_link}',
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success(`Sent ${data.sent} messages`, {
          description: data.failed ? `${data.failed} failed · logged to Analytics.` : 'Logged to Analytics.',
        });
        setAgents(prev => prev.map(a => a.id === agent.id ? {
          ...a,
          todayCalls: (a.todayCalls || 0) + (data.sent || 0),
          confirmed: (a.confirmed || 0) + Math.floor((data.sent || 0) * 0.4),
          lastRun: 'Just now',
        } : a));
      } catch (e) {
        toast.error('Campaign send failed', { description: 'Publish your booking widget and try again.' });
      } finally {
        setRunningCampaign(null);
      }
    })();
  };

  return (
    <div className="space-y-5" data-testid="echo-ai-agents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900" style={{ fontSize: 15, fontWeight: 500 }}>AI Agents</h1>
            <Badge className="text-[9px] border-0 bg-violet-100 text-violet-700 gap-1"><Sparkles className="h-2.5 w-2.5" />Powered by Echo</Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {twilioConnected && <Badge variant="outline" className="text-[9px] gap-1 border-emerald-200 text-emerald-600 bg-emerald-50"><div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />(512) 555-0192 · Active</Badge>}
          <Button size="sm" className="h-8 text-xs gap-1 bg-[#111111] hover:bg-[#000000]" onClick={() => { setTab('create'); }} data-testid="create-agent-btn">
            <Plus className="h-3 w-3" /> Create Agent
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0" style={{ borderBottom: '1px solid #ebebeb' }} data-testid="agents-tabs">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className="px-4 py-2.5 text-xs font-medium transition-colors"
            style={{ background: 'none', border: 'none', borderBottom: tab === t.id ? '2px solid #111' : '2px solid transparent', color: tab === t.id ? '#111' : '#bbb', cursor: 'pointer' }}
            data-testid={`agents-tab-${t.id}`}>{t.label}</button>
        ))}
      </div>

      {/* ═══ TAB 1: OVERVIEW ═══ */}
      {tab === 'overview' && (
        <div className="space-y-5" data-testid="agents-overview">
          {/* Stat cards */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'Agents active', value: agents.filter(a => a.status === 'running').length, sub: `${agents.filter(a => a.status === 'running').length} running` },
              { label: 'Calls today', value: agents.reduce((s, a) => s + a.todayCalls, 0), sub: '↑ vs yesterday' },
              { label: 'Texts sent today', value: '112', sub: '↑ vs yesterday' },
              { label: 'Tasks completed', value: agents.reduce((s, a) => s + a.confirmed + a.rescheduled, 0), sub: 'AI handled: 94%' },
            ].map((s, i) => (
              <div key={i} className="p-4 rounded-xl bg-white" style={{ border: '0.5px solid #ebebeb' }} data-testid={`stat-card-${i}`}>
                <p className="text-[10px] text-slate-400">{s.label}</p>
                <p className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans'] mt-0.5">{s.value}</p>
                <p className="text-[9px] text-slate-400 mt-0.5">{s.sub}</p>
              </div>
            ))}
          </div>

          {/* Agent cards */}
          <div className="grid grid-cols-2 gap-3">
            {agents.map(a => {
              const st = STATUS_PILL[a.status] || STATUS_PILL.running;
              return (
                <div key={a.id} className="p-4 rounded-xl bg-white" style={{ border: '0.5px solid #ebebeb' }} data-testid={`agent-card-${a.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <ChannelIcon channel={a.channel} />
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1" style={{ background: st.bg, color: st.color }}>
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: st.dot }} />{st.label}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-slate-900" style={{ fontWeight: 500 }}>{a.name}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{a.desc}</p>
                  <div className="border-t mt-3 pt-2" style={{ borderColor: '#f0f0f0' }}>
                    <p className="text-[10px] text-slate-400">Today: {a.todayCalls} calls · {a.confirmed} confirmed · {a.rescheduled} rescheduled</p>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => { setSelectedAgent(a); setTab('agents'); }}>Edit</Button>
                    <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => toggleAgent(a.id)}>{a.status === 'running' ? 'Pause' : 'Resume'}</Button>
                    {a.campaign === 'booking_link_outreach' && (
                      <Button
                        size="sm"
                        className="h-6 text-[10px] gap-1 bg-[#111] hover:bg-[#222] ml-auto"
                        onClick={() => runCampaign(a)}
                        disabled={runningCampaign === a.id}
                        data-testid={`agent-run-now-${a.id}`}
                      >
                        {runningCampaign === a.id
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <Zap className="h-3 w-3" />} Send now
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pre-built agents */}
          <div>
            <p className="text-[11px] text-slate-400 uppercase tracking-wide mb-2">Echo pre-built agents · Ready to deploy in one tap</p>
            <div className="flex gap-2.5 overflow-x-auto pb-2" data-testid="prebuilt-agents-scroll">
              {PRE_BUILT_AGENTS.map(tpl => (
                <div key={tpl.id} className="min-w-[220px] p-3.5 rounded-xl bg-white shrink-0 flex flex-col" style={{ border: '0.5px solid #ebebeb' }} data-testid={`prebuilt-${tpl.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <ChannelIcon channel={tpl.channel} />
                    {tpl.campaign === 'booking_link_outreach' && (
                      <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-slate-900 text-white font-medium inline-flex items-center gap-1" title="Auto-inserts your /book/ link">
                        <Sparkles className="h-2 w-2" /> Booking link
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-medium text-slate-900 mt-2" style={{ fontWeight: 500 }}>{tpl.name}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-2 flex-1">{tpl.desc}</p>
                  <button onClick={() => startFromTemplate(tpl)} className="text-[10px] text-[#111111] font-medium mt-2 flex items-center gap-1" style={{ background: 'none', border: 'none', cursor: 'pointer' }} data-testid={`prebuilt-add-${tpl.id}`}>
                    Add to my agents <ArrowRight className="h-2.5 w-2.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Live activity feed */}
          <div data-testid="activity-feed">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[11px] font-medium text-slate-700">Echo AI</span>
              <span className="flex items-center gap-1 text-[9px] text-emerald-600 font-medium"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />Live</span>
            </div>
            {isDemo ? (
              <div className="space-y-1">
                {DEMO_ACTIVITY_FEED.map((e, i) => (
                  <div key={i} className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-slate-50/50 transition-colors" data-testid={`feed-item-${i}`}>
                    <div className="h-2 w-2 rounded-full shrink-0" style={{ background: FEED_DOT[e.status] }} />
                    <span className="text-[10px] text-slate-500">{e.agent}</span>
                    <span className="text-[10px] text-slate-700">·</span>
                    <span className="text-[10px] text-slate-700">{e.action}</span>
                    <span className="text-[10px] text-slate-700">·</span>
                    <span className="text-[10px] text-slate-500">{e.result}</span>
                    <span className="text-[10px] text-slate-400 ml-auto shrink-0">{e.time}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-6 text-center rounded-lg border border-dashed border-slate-200" data-testid="activity-feed-empty">
                <p className="text-[11px] text-slate-500">No agent activity yet</p>
                <p className="text-[10px] text-slate-400 mt-0.5">Live events will appear here once your agents start running.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ TAB 2: ANALYTICS (per-agent perf + filters + CSV/print exports) ═══ */}
      {tab === 'analytics' && (
        <AgentAnalyticsPanel agents={agents} demoSends={demoCampaignSends} />
      )}

      {/* ═══ TAB 3: MY AGENTS ═══ */}
      {tab === 'agents' && (
        <div className="flex gap-4" data-testid="my-agents-tab">
          {/* Left sidebar */}
          <div className="w-60 shrink-0 space-y-2" data-testid="agents-sidebar">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
              <input type="text" placeholder="Search agents..." className="w-full pl-8 pr-3 py-2 rounded-lg text-xs outline-none" style={{ background: '#f9f9f9', border: '0.5px solid #ebebeb' }} />
            </div>
            <div className="flex gap-1">
              {['All', 'Running', 'Paused', 'Drafts'].map(f => (
                <button key={f} className="px-2 py-1 rounded-md text-[9px] font-medium text-slate-500 hover:bg-slate-100" style={{ border: 'none', cursor: 'pointer', background: f === 'All' ? '#f0f0f0' : 'none' }}>{f}</button>
              ))}
            </div>
            <div className="space-y-0.5">
              {agents.map(a => (
                <button key={a.id} onClick={() => setSelectedAgent(a)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-left transition-colors"
                  style={{ background: selectedAgent?.id === a.id ? '#f9f9f9' : 'transparent', borderLeft: selectedAgent?.id === a.id ? '2.5px solid #111111' : '2.5px solid transparent', border: 'none', cursor: 'pointer' }}
                  data-testid={`agent-sidebar-${a.id}`}>
                  <ChannelIcon channel={a.channel} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-medium text-slate-800 truncate">{a.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 tabular-nums" data-testid={`agent-chip-handled-${a.id}`}>{a.todayCalls}</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 tabular-nums" data-testid={`agent-chip-conv-${a.id}`}>{a.successRate}%</span>
                      <span className="text-[9px] text-slate-400 truncate">· {a.lastRun}</span>
                    </div>
                  </div>
                  <div className="h-2 w-2 rounded-full shrink-0" style={{ background: STATUS_PILL[a.status]?.dot }} />
                </button>
              ))}
            </div>
          </div>

          {/* Right detail panel */}
          <div className="flex-1 min-w-0">
            {selectedAgent ? (() => {
              const a = selectedAgent;
              const st = STATUS_PILL[a.status] || STATUS_PILL.running;
              return (
                <div className="space-y-4" data-testid="agent-detail">
                  <div className="flex items-center gap-3">
                    <p className="text-sm font-medium text-slate-900" style={{ fontWeight: 500 }}>{a.name}</p>
                    <span className="text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1" style={{ background: st.bg, color: st.color }}><span className="h-1.5 w-1.5 rounded-full" style={{ background: st.dot }} />{st.label}</span>
                    <div className="ml-auto flex gap-1.5">
                      <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1"><Pencil className="h-2.5 w-2.5" />Edit</Button>
                      <Button size="sm" className="h-7 text-[10px] gap-1 bg-[#111111] hover:bg-[#000000]"><Play className="h-2.5 w-2.5" />Run now</Button>
                      <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={() => toggleAgent(a.id)}>{a.status === 'running' ? <Pause className="h-2.5 w-2.5" /> : <Play className="h-2.5 w-2.5" />}</Button>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-500">{a.desc}</p>

                  {/* Performance */}
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-2">Performance (last 7 days)</p>
                    <div className="grid grid-cols-4 gap-3">
                      {[
                        { label: 'Contacts reached', value: a.todayCalls },
                        { label: 'Success rate', value: `${a.successRate}%` },
                        { label: 'Avg call length', value: a.avgCallLen },
                        { label: 'Appts affected', value: a.aptsAffected },
                      ].map((m, i) => (
                        <div key={i} className="p-3 rounded-xl" style={{ background: '#f9f9f9', border: '0.5px solid #ebebeb' }}>
                          <p className="text-[9px] text-slate-400">{m.label}</p>
                          <p className="text-lg font-bold text-slate-800 font-['Plus_Jakarta_Sans']">{m.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Settings summary */}
                  <div className="p-3.5 rounded-xl" style={{ background: '#f9f9f9', border: '0.5px solid #ebebeb' }}>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-2">Settings Summary</p>
                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                      <div><span className="text-slate-400">Trigger:</span> <span className="text-slate-700">{a.trigger}</span></div>
                      <div><span className="text-slate-400">Channel:</span> <span className="text-slate-700">{a.channel}</span></div>
                      <div><span className="text-slate-400">Voice:</span> <span className="text-slate-700">{a.voice}</span></div>
                      <div><span className="text-slate-400">Schedule:</span> <span className="text-slate-700">{a.schedule}</span></div>
                      <div><span className="text-slate-400">Escalate to:</span> <span className="text-slate-700">{a.escalate}</span></div>
                    </div>
                  </div>

                  {/* Recent activity */}
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-2">Recent Activity</p>
                    <div className="space-y-1">
                      {ACTIVITY_FEED.filter(e => e.agent.toLowerCase().includes(a.name.split(' ')[0].toLowerCase())).slice(0, 5).map((e, i) => (
                        <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-slate-50">
                          <div className="h-1.5 w-1.5 rounded-full" style={{ background: FEED_DOT[e.status] }} />
                          <span className="text-[10px] text-slate-700">{e.action} · {e.result}</span>
                          <span className="text-[10px] text-slate-400 ml-auto">{e.time}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })() : (
              <div className="text-center py-16">
                <Sparkles className="h-8 w-8 text-slate-200 mx-auto mb-3" />
                <p className="text-sm text-slate-400">Select an agent to view details</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ TAB 4: CREATE ═══ */}
      {tab === 'create' && (
        <div className="space-y-5" data-testid="create-tab">
          <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto">
            <div className="p-6 rounded-xl bg-white text-center cursor-pointer hover:border-[#111111] transition-colors" style={{ border: '0.5px solid #ebebeb' }}
              onClick={() => { setWizardMode('template'); setWizardStep(0); setWizardOpen(true); setDeployed(false); }} data-testid="create-from-template">
              <Sparkles className="h-6 w-6 text-[#111111] mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-900" style={{ fontWeight: 500 }}>Build from template</p>
              <p className="text-[11px] text-slate-500 mt-1">Pick one of Echo's pre-built agents and customize it. Fastest way to get started.</p>
              <p className="text-[10px] text-[#111111] font-medium mt-3">Browse templates <ArrowRight className="h-2.5 w-2.5 inline" /></p>
            </div>
            <div className="p-6 rounded-xl bg-white text-center cursor-pointer hover:border-[#111111] transition-colors" style={{ border: '0.5px solid #ebebeb' }}
              onClick={startFromScratch} data-testid="create-from-scratch">
              <div className="h-6 w-6 rounded border border-slate-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-900" style={{ fontWeight: 500 }}>Start from scratch</p>
              <p className="text-[11px] text-slate-500 mt-1">Tell Echo what you want the agent to do in plain language — we'll build it for you.</p>
              <p className="text-[10px] text-[#111111] font-medium mt-3">Start with blank <ArrowRight className="h-2.5 w-2.5 inline" /></p>
            </div>
          </div>
          <p className="text-[10px] text-slate-400 text-center">Most users start with a template and customize it.</p>

          {/* Template grid */}
          <div>
            <div className="flex gap-1.5 mb-3">
              {['All', 'Voice', 'SMS', 'Multi-channel'].map(f => (
                <button key={f} className="px-2.5 py-1 rounded-md text-[10px] font-medium" style={{ border: 'none', cursor: 'pointer', background: f === 'All' ? '#f0f0f0' : 'transparent', color: f === 'All' ? '#111' : '#bbb' }}>{f}</button>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2.5">
              {PRE_BUILT_AGENTS.map(tpl => (
                <div key={tpl.id} className="p-3.5 rounded-xl bg-white cursor-pointer hover:border-[#111111] transition-colors" style={{ border: '0.5px solid #ebebeb' }}
                  onClick={() => startFromTemplate(tpl)} data-testid={`tpl-${tpl.id}`}>
                  <ChannelIcon channel={tpl.channel} />
                  <p className="text-xs font-medium text-slate-900 mt-2" style={{ fontWeight: 500 }}>{tpl.name}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">{tpl.desc}</p>
                  <p className="text-[9px] text-slate-400 mt-1">Trigger: {tpl.trigger}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══ TAB 4: VOICE STUDIO ═══ */}
      {tab === 'voice' && (
        <VoiceStudio />
      )}

      {/* ═══ TAB 5: SETTINGS ═══ */}
      {tab === 'settings' && (
        <div className="space-y-5 max-w-2xl" data-testid="agents-settings">
          {/* Phone numbers */}
          <div className="p-4 rounded-xl bg-white" style={{ border: '0.5px solid #ebebeb' }}>
            <p className="text-xs font-medium text-slate-900 mb-3" style={{ fontWeight: 500 }}>Connected numbers</p>
            <div className="space-y-2">
              {[{ num: '(512) 555-0192', label: 'Main Office (Primary)' }, { num: '(512) 555-0877', label: 'Inbound line' }].map((ph, i) => (
                <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg" style={{ background: '#f9f9f9' }}>
                  <div className="h-2 w-2 rounded-full bg-emerald-500" />
                  <span className="text-xs text-slate-800">{ph.num}</span>
                  <span className="text-[10px] text-slate-400">· {ph.label}</span>
                  <div className="ml-auto flex gap-1"><Button variant="ghost" size="sm" className="h-6 text-[10px]">Edit</Button><Button variant="ghost" size="sm" className="h-6 text-[10px] text-red-500">Remove</Button></div>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" className="text-[10px] gap-1 mt-2" onClick={() => toast.info('Add number flow')}><Plus className="h-3 w-3" />Add another number</Button>
          </div>

          {/* Twilio account */}
          <div className="p-4 rounded-xl bg-white" style={{ border: '0.5px solid #ebebeb' }}>
            <p className="text-xs font-medium text-slate-900 mb-3" style={{ fontWeight: 500 }}>Twilio account</p>
            <div className="flex items-center gap-2 mb-3">
              <div className="h-2 w-2 rounded-full bg-emerald-500" />
              <span className="text-xs text-slate-700">Connected (Account SID: AC···1234)</span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-[11px]">
              <div className="p-2.5 rounded-lg" style={{ background: '#f9f9f9' }}><p className="text-slate-400 text-[9px]">Balance</p><p className="font-bold text-slate-800">$47.22</p></div>
              <div className="p-2.5 rounded-lg" style={{ background: '#f9f9f9' }}><p className="text-slate-400 text-[9px]">This month total</p><p className="font-bold text-slate-800">$16.36</p></div>
            </div>
            <div className="mt-2 space-y-1 text-[10px] text-slate-500">
              <div className="flex justify-between"><span>Voice minutes: 1,247 min</span><span>$12.47</span></div>
              <div className="flex justify-between"><span>SMS messages: 3,891</span><span>$3.89</span></div>
              <div className="flex justify-between"><span>Email: 2,100</span><span>$0.00 (included)</span></div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button variant="outline" size="sm" className="text-[10px]" onClick={() => toast.info('Top up balance')}>Top up balance</Button>
              <Button variant="outline" size="sm" className="text-[10px]">View Twilio dashboard</Button>
            </div>
          </div>

          {/* Notifications */}
          <div className="p-4 rounded-xl bg-white" style={{ border: '0.5px solid #ebebeb' }}>
            <p className="text-xs font-medium text-slate-900 mb-3" style={{ fontWeight: 500 }}>Notifications</p>
            <div className="space-y-2">
              {[
                { label: 'Agent can\'t handle a call', sub: 'Alert front desk immediately', on: true },
                { label: 'Patient requests a human', sub: 'Alert front desk immediately', on: true },
                { label: 'No-show confirmed', sub: 'Update Front Desk mode', on: true },
                { label: 'Agent completes a task', sub: 'Show in activity feed only', on: false },
                { label: 'Daily agent summary', sub: 'Send to admin@valleymed.com', on: false },
              ].map((n, i) => (
                <div key={i} className="flex items-center justify-between py-1.5">
                  <div><p className="text-[11px] text-slate-700">{n.label}</p><p className="text-[9px] text-slate-400">{n.sub}</p></div>
                  <button className={cn("relative inline-flex h-5 w-9 items-center rounded-full transition-colors", n.on ? "bg-[#111111]" : "bg-slate-200")} style={{ border: 'none', cursor: 'pointer' }}>
                    <span className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform" style={{ transform: n.on ? 'translateX(18px)' : 'translateX(2px)' }} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Team permissions */}
          <div className="p-4 rounded-xl bg-white" style={{ border: '0.5px solid #ebebeb' }}>
            <p className="text-xs font-medium text-slate-900 mb-3" style={{ fontWeight: 500 }}>Team permissions</p>
            <p className="text-[10px] text-slate-400 mb-2">Who can create and edit agents:</p>
            <div className="space-y-1">
              {['Only practice administrators', 'All staff members', 'Custom (configure per user)'].map((opt, i) => (
                <label key={i} className="flex items-center gap-2 py-1.5 cursor-pointer">
                  <input type="radio" name="perms" defaultChecked={i === 1} className="h-3.5 w-3.5" style={{ accentColor: '#111' }} />
                  <span className="text-[11px] text-slate-700">{opt}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══ WIZARD DIALOG ═══ */}
      <Dialog open={wizardOpen} onOpenChange={(v) => { if (!v) setWizardOpen(false); }}>
        <DialogContent className="sm:max-w-3xl p-0 gap-0 overflow-hidden max-h-[92vh] overflow-y-auto" data-testid="agent-wizard">
          <DialogTitle className="sr-only">Agent Builder</DialogTitle>
          <DialogDescription className="sr-only">Create and configure a new AI agent</DialogDescription>
          {/* Progress dots */}
          <div className="flex items-center gap-1.5 px-6 pt-5 pb-2">
            {[1,2,3,4,5,6,7].map(s => (
              <div key={s} className={cn("h-1.5 rounded-full transition-all", wizardStep >= s ? "bg-[#111111] w-6" : "bg-slate-200 w-1.5")} />
            ))}
            <span className="text-[9px] text-slate-400 ml-2">Step {wizardStep}/7</span>
          </div>

          {/* Step 0: Template picker */}
          {wizardStep === 0 && (
            <div className="px-6 pb-5 space-y-3">
              <p className="text-sm font-medium text-slate-900" style={{ fontWeight: 500 }}>Choose a template</p>
              <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                {PRE_BUILT_AGENTS.map(tpl => (
                  <button key={tpl.id} onClick={() => startFromTemplate(tpl)} className="p-3 rounded-xl text-left hover:border-[#111111] transition-colors" style={{ border: '0.5px solid #ebebeb', background: '#fff', cursor: 'pointer' }}>
                    <ChannelIcon channel={tpl.channel} />
                    <p className="text-[11px] font-medium text-slate-800 mt-1">{tpl.name}</p>
                    <p className="text-[9px] text-slate-400">{tpl.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 1: Name */}
          {wizardStep === 1 && (
            <div className="px-6 pb-5 space-y-3">
              <p className="text-sm font-medium text-slate-900" style={{ fontWeight: 500 }}>What should we call this agent?</p>
              <input type="text" value={wName} onChange={e => setWName(e.target.value)} placeholder="Appointment Reminder Agent"
                className="w-full px-3 py-2.5 rounded-lg text-sm outline-none" style={{ background: '#f9f9f9', border: '0.5px solid #ebebeb' }} data-testid="wizard-name" />
              <p className="text-[10px] text-slate-400">For your internal use only — patients won't see it.</p>
              <Button className="w-full h-10 text-xs bg-[#111111] hover:bg-[#000000]" onClick={() => setWizardStep(2)} disabled={!wName}>Continue <ArrowRight className="h-3 w-3 ml-1" /></Button>
            </div>
          )}

          {/* Step 2: Job */}
          {wizardStep === 2 && (
            <div className="px-6 pb-5 space-y-3">
              <p className="text-sm font-medium text-slate-900" style={{ fontWeight: 500 }}>What's the main job of this agent?</p>
              <div className="grid grid-cols-2 gap-2">
                {JOBS.map(j => (
                  <button key={j.id} onClick={() => { setWJob(j.id); setWizardStep(3); }}
                    className="p-4 rounded-xl text-left transition-colors" style={{ border: wJob === j.id ? '1px solid #111111' : '0.5px solid #ebebeb', background: wJob === j.id ? '#f5f3ff' : '#fff', cursor: 'pointer' }} data-testid={`wizard-job-${j.id}`}>
                    <j.icon className="h-4 w-4 text-slate-500 mb-1.5" />
                    <p className="text-[11px] font-medium text-slate-800">{j.label}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Channel */}
          {wizardStep === 3 && (
            <div className="px-6 pb-5 space-y-3">
              <p className="text-sm font-medium text-slate-900" style={{ fontWeight: 500 }}>How will this agent reach patients?</p>
              {[{ id: 'voice', icon: Phone, label: 'Phone call', desc: 'Talk to patients directly. Best for confirmations.' },
                { id: 'sms', icon: MessageSquareText, label: 'Text message (SMS)', desc: 'Quick, asynchronous. Best for reminders + links.' },
                { id: 'email', icon: Mail, label: 'Email', desc: 'Best for forms, documents, and detailed info.' }].map(ch => (
                <button key={ch.id} onClick={() => setWChannels(prev => prev.includes(ch.id) ? prev.filter(c => c !== ch.id) : [...prev, ch.id])}
                  className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors" style={{ border: wChannels.includes(ch.id) ? '1px solid #111111' : '0.5px solid #ebebeb', background: wChannels.includes(ch.id) ? '#f5f3ff' : '#fff', cursor: 'pointer' }}>
                  <ch.icon className="h-4 w-4 text-slate-500" />
                  <div><p className="text-[11px] font-medium text-slate-800">{ch.label}</p><p className="text-[9px] text-slate-400">{ch.desc}</p></div>
                  {wChannels.includes(ch.id) && <CheckCircle2 className="h-4 w-4 text-[#111111] ml-auto" />}
                </button>
              ))}
              <Button className="w-full h-10 text-xs bg-[#111111] hover:bg-[#000000]" onClick={() => setWizardStep(wChannels.includes('voice') ? 4 : 5)} disabled={wChannels.length === 0}>Continue <ArrowRight className="h-3 w-3 ml-1" /></Button>
            </div>
          )}

          {/* Step 4: Voice */}
          {wizardStep === 4 && (
            <div className="px-6 pb-5 space-y-3">
              <p className="text-sm font-medium text-slate-900" style={{ fontWeight: 500 }}>Choose a voice for this agent</p>
              <p className="text-[11px] text-slate-500">Patients will hear this voice when the agent calls them.</p>
              <div className="grid grid-cols-2 gap-2">
                {VOICES.map(v => (
                  <button key={v.id} onClick={() => setWVoice(v.id)}
                    className="p-3.5 rounded-xl text-left transition-colors" style={{ border: wVoice === v.id ? '1px solid #111111' : '0.5px solid #ebebeb', background: wVoice === v.id ? '#f5f3ff' : '#fff', cursor: 'pointer' }} data-testid={`wizard-voice-${v.id}`}>
                    <p className="text-[11px] font-medium text-slate-800">{v.name}</p>
                    <p className="text-[9px] text-slate-400">{v.desc}</p>
                    <span className="text-[9px] text-[#111111] font-medium mt-1 flex items-center gap-1 cursor-pointer" onClick={e => { e.stopPropagation(); toast.info('Playing voice sample...'); }}>
                      <Play className="h-2.5 w-2.5" />Play sample
                    </span>
                  </button>
                ))}
              </div>
              <Button className="w-full h-10 text-xs bg-[#111111] hover:bg-[#000000]" onClick={() => setWizardStep(5)}>Continue <ArrowRight className="h-3 w-3 ml-1" /></Button>
            </div>
          )}

          {/* Step 5: Script */}
          {wizardStep === 5 && (
            <div className="px-6 pb-5 space-y-3">
              <p className="text-sm font-medium text-slate-900" style={{ fontWeight: 500 }}>What should the agent say?</p>
              <p className="text-[11px] text-slate-500">Echo handles the conversation naturally — just tell it the key points.</p>
              <textarea value={wScript} onChange={e => setWScript(e.target.value)} rows={8}
                className="w-full px-3 py-2.5 rounded-xl text-xs outline-none resize-none leading-relaxed" style={{ background: '#f9f9f9', border: '0.5px solid #ebebeb' }} data-testid="wizard-script" />
              <div className="flex flex-wrap gap-1">
                {VARIABLES.map(v => (
                  <button key={v} onClick={() => setWScript(prev => prev + ` [${v}]`)} className="px-2 py-0.5 rounded-md text-[9px] font-medium" style={{ background: '#f5f3ff', color: '#111111', border: 'none', cursor: 'pointer' }}>[{v}]</button>
                ))}
              </div>
              <Button className="w-full h-10 text-xs bg-[#111111] hover:bg-[#000000]" onClick={() => setWizardStep(6)}>Continue <ArrowRight className="h-3 w-3 ml-1" /></Button>
            </div>
          )}

          {/* Step 6: Trigger & Schedule */}
          {wizardStep === 6 && (
            <div className="px-6 pb-5 space-y-3">
              <p className="text-sm font-medium text-slate-900" style={{ fontWeight: 500 }}>When should this agent activate?</p>
              {[{ id: 'auto', label: 'Automatically — based on a schedule' }, { id: 'manual', label: 'Manually — I\'ll trigger it myself' }, { id: 'inbound', label: 'When a patient contacts us (inbound)' }].map(t => (
                <button key={t.id} onClick={() => setWTrigger(t.id)} className="w-full flex items-center gap-3 p-3 rounded-xl text-left" style={{ border: wTrigger === t.id ? '1px solid #111111' : '0.5px solid #ebebeb', background: wTrigger === t.id ? '#f5f3ff' : '#fff', cursor: 'pointer' }}>
                  <div className="h-4 w-4 rounded-full border-2 flex items-center justify-center" style={{ borderColor: wTrigger === t.id ? '#111111' : '#ddd' }}>
                    {wTrigger === t.id && <div className="h-2 w-2 rounded-full bg-[#111111]" />}
                  </div>
                  <span className="text-[11px] text-slate-800">{t.label}</span>
                </button>
              ))}
              <div className="pt-2">
                <p className="text-[10px] text-slate-500 mb-1.5">Operating days:</p>
                <div className="flex gap-1">
                  {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
                    <button key={d} onClick={() => setWDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d])}
                      className="h-7 w-7 rounded-full text-[9px] font-medium flex items-center justify-center" style={{ background: wDays.includes(d) ? '#111' : 'transparent', color: wDays.includes(d) ? '#fff' : '#bbb', border: wDays.includes(d) ? 'none' : '1px solid #ddd', cursor: 'pointer' }}>{d.charAt(0)}</button>
                  ))}
                </div>
              </div>
              <Button className="w-full h-10 text-xs bg-[#111111] hover:bg-[#000000]" onClick={() => setWizardStep(7)}>Continue <ArrowRight className="h-3 w-3 ml-1" /></Button>
            </div>
          )}

          {/* Step 7: Review & Deploy */}
          {wizardStep === 7 && !deployed && (
            <div className="px-6 pb-5 space-y-4">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#111111]" />
                <p className="text-sm font-medium text-slate-900" style={{ fontWeight: 500 }}>Ready to deploy</p>
              </div>
              <p className="text-lg font-bold text-slate-900 font-['Plus_Jakarta_Sans']">{wName}</p>

              {/* ── Agentic quick-run input ── */}
              <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid="wizard-quickrun">
                <div className="flex items-center gap-1.5">
                  <Zap className="h-3.5 w-3.5 text-[#111111]" />
                  <p className="text-[11px] font-semibold text-slate-800">Agentic quick-run (optional)</p>
                </div>
                <p className="text-[10px] text-slate-500">
                  Kick this agent off once on a custom slice. Example: <i>&ldquo;Run this agent for today&rsquo;s appointments&rdquo;</i> or <i>&ldquo;Run for appointments 5 days out.&rdquo;</i>
                </p>
                <input
                  value={wQuickRun}
                  onChange={(e) => setWQuickRun(e.target.value)}
                  placeholder='Describe in plain English…'
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[12px] outline-none focus:border-slate-900"
                  data-testid="wizard-quickrun-input"
                />
                <div className="flex flex-wrap gap-1.5">
                  {[
                    'Run for today',
                    'Run for tomorrow',
                    'Run for appointments 5 days out',
                    'Run for high-risk only',
                  ].map((t) => (
                    <button key={t} onClick={() => setWQuickRun(t)}
                      className="text-[9px] px-2 py-0.5 rounded-full border border-slate-200 text-slate-600 bg-slate-50 hover:bg-slate-100"
                      style={{ cursor: 'pointer' }}
                      data-testid={`wizard-quickrun-preset-${t.replace(/\W+/g, '-').toLowerCase()}`}
                    >{t}</button>
                  ))}
                </div>
              </div>

              {/* ── Automated workflows (queued-action UI only) ── */}
              <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid="wizard-workflows">
                <div className="flex items-center gap-1.5">
                  <Workflow className="h-3.5 w-3.5 text-[#111111]" />
                  <p className="text-[11px] font-semibold text-slate-800">Automated workflows</p>
                  <span className="ml-auto text-[9px] text-slate-400">Actions are queued for review — no auto-cancellations in demo.</span>
                </div>
                {/* Suggested preset dropdown */}
                <div className="flex items-center gap-2" data-testid="wizard-workflow-presets">
                  <span className="text-[10px] text-slate-500">Start from a preset:</span>
                  <select
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) return;
                      if (v === 'confirm-blitz') {
                        setWWorkflows({ notConfirmedCancel: true, notConfirmedHours: 4, voicemailRetryText: true, escalateIfRisk: true });
                        if (wCustomFields.length === 0) setWCustomFields([{ key: `cf-${Date.now()}`, label: 'confirm_intent', kind: 'select', value: 'Confirmed, Reschedule, Cancel' }]);
                      } else if (v === 'postvisit-loop') {
                        setWWorkflows({ notConfirmedCancel: false, notConfirmedHours: 4, voicemailRetryText: true, escalateIfRisk: false });
                        if (wCustomFields.length === 0) setWCustomFields([{ key: `cf-${Date.now()}`, label: 'review_link', kind: 'text', value: '' }, { key: `cf-${Date.now()}+1`, label: 'followup_offer', kind: 'bool', value: true }]);
                      } else if (v === 'denial-rescue') {
                        setWWorkflows({ notConfirmedCancel: false, notConfirmedHours: 12, voicemailRetryText: true, escalateIfRisk: true });
                        if (wCustomFields.length === 0) setWCustomFields([{ key: `cf-${Date.now()}`, label: 'denial_code', kind: 'text', value: '' }, { key: `cf-${Date.now()}+1`, label: 'appeal_template', kind: 'select', value: 'CO-45, CO-97, PR-204' }]);
                      } else if (v === 'care-gap') {
                        setWWorkflows({ notConfirmedCancel: false, notConfirmedHours: 24, voicemailRetryText: true, escalateIfRisk: false });
                        if (wCustomFields.length === 0) setWCustomFields([{ key: `cf-${Date.now()}`, label: 'gap_type', kind: 'select', value: 'AWV, HbA1c, Colonoscopy, Mammogram' }]);
                      } else if (v === 'clear') {
                        setWWorkflows({ notConfirmedCancel: false, notConfirmedHours: 4, voicemailRetryText: false, escalateIfRisk: false });
                        setWCustomFields([]);
                      }
                      toast.success(`Applied preset · ${v.replace(/-/g, ' ')}`);
                      e.target.value = '';
                    }}
                    defaultValue=""
                    className="text-[10px] px-2 py-1 rounded border border-slate-200 outline-none bg-white"
                    data-testid="wizard-workflow-preset-select"
                  >
                    <option value="">Choose a preset…</option>
                    <option value="confirm-blitz">Confirm blitz (reminder + escalate + cancel)</option>
                    <option value="postvisit-loop">Post-visit loop (review + follow-up offer)</option>
                    <option value="denial-rescue">Payer denial rescue</option>
                    <option value="care-gap">Care-gap outreach</option>
                    <option value="clear">— Clear all —</option>
                  </select>
                </div>
                <label className="flex items-start gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={wWorkflows.notConfirmedCancel} onChange={(e) => setWWorkflows({ ...wWorkflows, notConfirmedCancel: e.target.checked })} className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 accent-slate-900" data-testid="wf-notconfirmed-cancel" />
                  <div className="flex-1">
                    <p className="text-[11px] font-medium text-slate-800">If patient doesn&rsquo;t confirm → queue appointment for cancellation</p>
                    <div className="text-[10px] text-slate-500 mt-0.5 inline-flex items-center gap-1">
                      Threshold:
                      <select
                        value={wWorkflows.notConfirmedHours}
                        onChange={(e) => setWWorkflows({ ...wWorkflows, notConfirmedHours: parseInt(e.target.value, 10) })}
                        className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-white outline-none"
                        data-testid="wf-notconfirmed-hours"
                      >
                        <option value={2}>2 hours before</option>
                        <option value={4}>4 hours before</option>
                        <option value={12}>12 hours before</option>
                        <option value={24}>24 hours before</option>
                      </select>
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={wWorkflows.voicemailRetryText} onChange={(e) => setWWorkflows({ ...wWorkflows, voicemailRetryText: e.target.checked })} className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 accent-slate-900" data-testid="wf-voicemail-retry" />
                  <div className="flex-1">
                    <p className="text-[11px] font-medium text-slate-800">If voicemail → auto-send a text as retry</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">Uses the same persona + visit-type variables.</p>
                  </div>
                </label>
                <label className="flex items-start gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={wWorkflows.escalateIfRisk} onChange={(e) => setWWorkflows({ ...wWorkflows, escalateIfRisk: e.target.checked })} className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 accent-slate-900" data-testid="wf-escalate-risk" />
                  <div className="flex-1">
                    <p className="text-[11px] font-medium text-slate-800">Escalate to front desk when no-show risk ≥ 50%</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">Creates a punch-list task instead of waiting on patient.</p>
                  </div>
                </label>
              </div>

              {/* ── Custom personalization fields ── */}
              <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid="wizard-custom-fields">
                <div className="flex items-center gap-1.5">
                  <Settings className="h-3.5 w-3.5 text-[#111111]" />
                  <p className="text-[11px] font-semibold text-slate-800">Custom personalization fields</p>
                  <Button size="sm" variant="outline" className="ml-auto h-6 text-[10px] gap-1" onClick={() => setWCustomFields((prev) => [...prev, { key: `cf-${Date.now()}`, label: '', kind: 'text', value: '' }])} data-testid="wizard-add-cf">
                    <Plus className="h-3 w-3" /> Add field
                  </Button>
                </div>
                <p className="text-[10px] text-slate-500">User-defined variables your agent can use in scripts. Example: <code className="bg-slate-100 px-1 rounded">copay_waiver</code>, <code className="bg-slate-100 px-1 rounded">language</code>, <code className="bg-slate-100 px-1 rounded">transport_note</code>.</p>
                {wCustomFields.length === 0 ? (
                  <p className="text-[10px] text-slate-400 italic">No custom fields — click &ldquo;Add field&rdquo; to create one.</p>
                ) : (
                  <div className="space-y-1.5">
                    {wCustomFields.map((cf, idx) => (
                      <div key={cf.key} className="grid grid-cols-[1fr_1fr_2fr_auto] gap-1.5 items-center" data-testid={`wizard-cf-${idx}`}>
                        <input
                          value={cf.label}
                          onChange={(e) => setWCustomFields((prev) => prev.map((x, i) => (i === idx ? { ...x, label: e.target.value } : x)))}
                          placeholder="Label"
                          className="text-[11px] px-2 py-1.5 rounded border border-slate-200 outline-none focus:border-slate-900"
                        />
                        <select
                          value={cf.kind}
                          onChange={(e) => setWCustomFields((prev) => prev.map((x, i) => (i === idx ? { ...x, kind: e.target.value, value: e.target.value === 'bool' ? false : '' } : x)))}
                          className="text-[11px] px-2 py-1.5 rounded border border-slate-200 outline-none bg-white"
                        >
                          <option value="text">Text</option>
                          <option value="select">Dropdown</option>
                          <option value="bool">Toggle</option>
                        </select>
                        {cf.kind === 'bool' ? (
                          <label className="inline-flex items-center gap-2 text-[10px] text-slate-600 px-2">
                            <input type="checkbox" checked={!!cf.value} onChange={(e) => setWCustomFields((prev) => prev.map((x, i) => (i === idx ? { ...x, value: e.target.checked } : x)))} className="h-3.5 w-3.5" />
                            {cf.value ? 'On' : 'Off'}
                          </label>
                        ) : (
                          <input
                            value={cf.value || ''}
                            onChange={(e) => setWCustomFields((prev) => prev.map((x, i) => (i === idx ? { ...x, value: e.target.value } : x)))}
                            placeholder={cf.kind === 'select' ? 'Option1, Option2, …' : 'Default value'}
                            className="text-[11px] px-2 py-1.5 rounded border border-slate-200 outline-none focus:border-slate-900"
                          />
                        )}
                        <button onClick={() => setWCustomFields((prev) => prev.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-red-600 p-1" style={{ background: 'none', border: 'none', cursor: 'pointer' }} aria-label="Remove field">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-3.5 rounded-xl space-y-2 text-[11px]" style={{ background: '#f9f9f9', border: '0.5px solid #ebebeb' }}>
                <div className="flex justify-between"><span className="text-slate-400">Channel</span><span className="text-slate-800">{wChannels.join(' + ') || 'Voice'}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Voice</span><span className="text-slate-800">{VOICES.find(v => v.id === wVoice)?.name}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Trigger</span><span className="text-slate-800">{wTrigger === 'auto' ? 'Automatic' : wTrigger === 'manual' ? 'Manual' : 'Inbound'}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Schedule</span><span className="text-slate-800">{wDays.join(', ')}, 9am-6pm</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Workflows</span><span className="text-slate-800">{[wWorkflows.notConfirmedCancel && 'Cancel-on-noconfirm', wWorkflows.voicemailRetryText && 'VM→SMS retry', wWorkflows.escalateIfRisk && 'Escalate on risk'].filter(Boolean).join(' · ') || 'None'}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Custom fields</span><span className="text-slate-800">{wCustomFields.length}</span></div>
              </div>
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-emerald-50">
                <div className="h-2 w-2 rounded-full bg-emerald-500" />
                <span className="text-xs text-emerald-700">(512) 555-0192 · Main Office</span>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" size="sm" className="text-xs" onClick={() => setWizardStep(1)}>Edit</Button>
                {wQuickRun && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs gap-1"
                    onClick={() => toast.success(`Queued agentic run: "${wQuickRun}"`)}
                    data-testid="wizard-queue-quickrun"
                  >
                    <Zap className="h-3 w-3" /> Queue quick-run
                  </Button>
                )}
                <Button className="flex-1 h-10 text-xs bg-[#111111] hover:bg-[#000000] gap-1" onClick={deployAgent} disabled={deploying} data-testid="deploy-agent-btn">
                  {deploying ? <><Loader2 className="h-3 w-3 animate-spin" />Activating...</> : <>Deploy agent <ArrowRight className="h-3 w-3" /></>}
                </Button>
              </div>
            </div>
          )}

          {/* Deployed success */}
          {wizardStep === 7 && deployed && (
            <div className="px-6 pb-5 text-center space-y-4" data-testid="agent-deployed">
              <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
              <p className="text-sm font-semibold text-slate-900">Your agent is live.</p>
              <p className="text-[11px] text-slate-500">{wName} is now running and will begin handling tasks automatically.</p>
              <Button className="text-xs bg-[#111] hover:bg-[#222]" onClick={() => { setWizardOpen(false); setTab('agents'); }}>View in My Agents <ArrowRight className="h-3 w-3 ml-1" /></Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EchoAIAgents;
